import { useRef, useState, Fragment, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider
} from "@mui/material";

import { Button } from "@gpstrackit/react-controls";
import DataView from 'Components/DataView';
import ChipStatus from 'Components/ChipStatus';

//Helpers
import { getDateFormatted } from 'Core/data/Helpers';

const Reports = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef();
    const navigate = useNavigate();

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    //Local states
    const [total, setTotal] = useState(0);
    const [columns, setColumns] = useState([
        {
            key: "name",
            field: "name",
            title: messages['reports_main_report'],
            width: 200,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "reports_main_report",
            render: (item) => {
                return <span>{item?.name}</span>
            }
        },
        {
            key: "description",
            field: "description",
            title: messages['reports_main_description'],
            width: 300,
            show: true,
            sort: true,
            resize: true,
            export: true,
            functionString: 'return row?.description',
            code: "reports_main_description",
            render: (item) => {
                return <span>{item?.description}</span>
            }
        },
        {
            key: "platform",
            field: "platform",
            title: messages['reports_main_platform'],
            width: 200,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.platform',
            code: "reports_main_platform",
            render: (item) => {
                return <span>{item?.platform}</span>
            }
        },
        {
            key: "status",
            field: "status",
            title: messages['reports_main_status'],
            width: 150,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.status',
            code: "reports_main_status",
            render: (item) => {
                return <ChipStatus label={messages[item?.status] || item?.status} />
            }
        },
        {
            key: "createdOn",
            field: "createdOn",
            title: messages['reports_main_created'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
                let currentTimezone = '${timeZone}'

                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.createdOn ? formatDateWithTimezone(row.createdOn, row?.timezone || currentTimezone ) : ''
            `,
            code: "reports_main_created",
            render: (item) => {
                return <span>{item?.createdOn && getDateFormatted(item?.createdOn, "date", "datetime")}</span>
            }
        },
        {
            key: "updatedOn",
            field: "updatedOn",
            title: messages['reports_main_updated'],
            width: 250,
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: `
                let currentTimezone = '${timeZone}'
                function formatDateWithTimezone(date, tz) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).tz(tz).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.updatedOn ? formatDateWithTimezone(row.updatedOn, row?.timezone || currentTimezone ) : ''
            `,
            code: "reports_main_updated",
            render: (item) => {
                return <span>{item?.updatedOn && getDateFormatted(item?.updatedOn, "date", "datetime")}</span>
            }
        },
        {
            key: "execute",
            title: messages['reports_main_execute'],
            width: 250,
            show: true,
            locked: false,
            code: "reports_main_execute",
            render: (item) => {
                return (
                    <Fragment>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => {
                                if (item?.reportKey === "scheduled_reports") {
                                    navigate(`/app/reports/scheduled_reports`);
                                } else {
                                    navigate(`/app/reports/overview/${item.id}`);
                                }
                            }}
                        >
                            {messages['reports_main_execute']}
                        </Button>
                    </Fragment>
                )
            }
        }
    ])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['reports_main_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Button
                            className="btn-dark-blue"
                            onClick={() => navigate('/app/reports/exporthistory')}
                        >
                            {messages['reports_export_history']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        exportName="reports"
                        columns={columns}
                        dinamicQuery
                        name="pm-reports-list-v2"
                        entity="reportsmain"
                        graphqlFunction="searchReports"
                        graphqlSort="ReportSortInput"
                        graphqlConditions="ReportSearchInput"
                        customQuery="id reportKey dealerIds"
                        noSplit
                        noFilters
                        topheight={210}
                        onChangeColumns={(columns) => {
                            let currentColumns = columns.map((item) => ({
                                ...item,
                                title: messages[item.code],
                                render: item.key === "execute" ? (item) => {
                                    return (
                                        <Fragment>
                                            <Button
                                                className="btn-dark-blue"
                                                onClick={() => {
                                                    if (item?.reportKey === "scheduled_reports") {
                                                        navigate(`/app/reports/scheduled_reports`);
                                                    } else {
                                                        navigate(`/app/reports/overview/${item.id}`);
                                                    }
                                                }}
                                            >
                                                {messages['reports_main_execute']}
                                            </Button>
                                        </Fragment>
                                    )
                                } : item.key === "status" ? (item) => {
                                    return <ChipStatus label={messages[item?.status] || item?.status} />
                                } : item.render
                            }))
                            setColumns(currentColumns)
                        }}
                        disableItemSelection
                        //search
                        asyncSelect
                        searchConditions={["name", "description", "platform"]}
                        searchOptionValue="id"
                        searchOptionLabel="name"
                        customSearch={{ fields: ["status"], terms: ["Active"], exact: true }}
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags;
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }
                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name' && item?.fields[0] !== 'status');

                            let tagsById = currentTags.filter((item) => typeof item.id === "number");
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item.name))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: ["name", "description", "platform"], terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            currentConditions.push({ fields: ["status"], terms: ["Active"], exact: true, conditionOperator: "AND" });

                            if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions);
                        }}
                        searchFields={["name", "description", "platform"]}
                        tableButtons={["export", 'orderColumn']}
                        onChangeTotal={(total) => setTotal(total)}
                    />
                </Grid>
            </Paper>
        </Container>
    )
}
export default Reports;